import * as angular from 'angular';
import { Growl } from 'angular/custom/services/growl.service';
import { ConversionService } from 'app/_directives/conversionService';
import { integrateUibModelInReact } from 'app/_services/confirm-modal.factory';
import { Debounce } from 'app/_services/debounce.factory';
import { ErrorHandler } from 'app/_services/error-handler.factory';
import { Decimal } from 'decimal.js';

(function () {
    'use strict';

    /**
     * @ngdoc directive
     * @name pkpYourBiddingCurve
     * @memberof app
     *
     * @restrict E
     * @scope {}
     *
     * @description
     * This is for long term auction bidding to create an active bid (when the auction is open) and future bids.
     *
     * @param {Object} auction
     * @param {app.OrderCurvePointDto[]} [unifiedBids] Unified bids are a unification of future bids and an active bid
     *  (curve points and regular bid). This should be passed when in editing mode.
     * @param {Object} config
     * @param {function} onAnyAmountChange Calls the function whenever the user changes the amount of a curve point
     *  or an active bid.
     * @param {function} onUndiscountedChange Calls the function whenever the user changes the undiscounted checkbox.
     *
     * @author flzemke.btc
     */
    angular.module('app').directive('pkpYourBiddingCurve', pkpYourBiddingCurve);

    function pkpYourBiddingCurve(
        $window,
        $filter,
        $location: ng.ILocationService,
        auctionsOverviewService,
        authService,
        auctionResource,
        DATES,
        conversionService: ConversionService,
        INCLUDE,
        growl: Growl,
        $uibModal,
        errorHandler: ErrorHandler,
        debounce: Debounce
    ) {
        return {
            restrict: 'E',
            scope: {
                auction: '=',
                unifiedBids: '=',
                onAnyAmountChange: '&',
                onUndiscountedChange: '&',
                config: '='
            },
            template:
                require('app/_directives/pkp-your-bidding-curve.directive.html'),
            link: linkFn
        };

        function linkFn(scope) {
            scope.MINIMUM_BID_INFO_BLAH =
                auctionsOverviewService.MINIMUM_BID_INFO_BLAH;
            scope.INCLUDE = INCLUDE;
            scope.DATES = DATES;
            scope.PAGE = auctionsOverviewService.PAGE;
            scope.isLoggedIn = authService.isLoggedIn();
            scope.uiLoadingAuction = true;
            scope.uiLoadingAuctionError = false;
            scope.uiSubmitting = false;
            scope.uiSubmitAuctionSuccess = null;
            scope.uiCheckPage = false;
            scope.bid = { orderCurve: [] };
            scope.showUpgradeSettings = false;
            scope.showConversionSettings = false;
            scope.isUpgradePossible = conversionService.isUpgradePossible(
                scope.config,
                scope.auction.capacityCategory0,
                scope.auction.capacityCategory1
            );
            scope.isConversionPossible = conversionService.isConversionPossible(
                scope.config,
                scope.auction.networkPoint.bundled
            );
            scope.maxBid = auctionsOverviewService.calcMaxLongTermBid(
                scope.auction.marketable,
                scope.config.maximumOrderQuantity,
                scope.auction.auctionWindow,
                scope.auction.windows,
                true
            );
            scope.addAnotherSurcharge = addAnotherSurcharge;
            scope.removeSurchargeStep = removeSurchargeStep;
            scope.submitBid = submitBid;
            scope.findMaxAllowedQuantity = findMaxAllowedQuantity;
            scope.disableUpgrades = disableUpgrades;
            scope.disableConversions = disableConversions;
            scope.goBack = goBack;
            scope.onConversionAmountChange = onConversionAmountChange;
            scope.nonUndiscountedTariffsSupported =
                scope.auction.networkPoint.undiscountedTariffs &&
                scope.auction.networkPoint.connectionPointType == 'RESERVOIR';

            const validateFirstOrderQuantityDebounced: () => angular.IPromise<any> =
                debounce(
                    () =>
                        scope.bidForm.orderQuantity0 != null &&
                        scope.bidForm.orderQuantity0.$validate(),
                    500
                );

            scope.normalizedPeriodType =
                auctionsOverviewService.getPeriodTypeByValue(
                    scope.auction.periodType
                ).NORMALIZED;
            scope.callContext =
                ($location.search().from as string) === 'table'
                    ? 'Auction table'
                    : 'Your bids';

            /**
             * Depending on whether {@link scope.unifiedBids} is defined or not, the user is in edit mode or not.
             */
            (function init() {
                // If future bids are set, the user is in edit mode.
                if (!angular.isUndefined(scope.unifiedBids)) {
                    prePopulateForm(scope.unifiedBids);
                    scope.isEdit = true;
                } else {
                    scope.isEdit = false;
                    var orderCurvePoint: any = {};

                    if (scope.auction.phase === 'OPEN') {
                        orderCurvePoint.active = true;
                        orderCurvePoint.price = scope.auction.auctionSurcharge;
                    }

                    addAnotherSurcharge(orderCurvePoint);
                }
            })();

            /**
             * Submit the form and send a request to the back-end if validation succeeded and {@link scope.uiCheckPage} is true.
             *
             * @param {boolean} isValid A boolean representing the validation of the form.
             * @returns {boolean} true if a call to the back-end service was made, false otherwise.
             */
            function submitBid(isValid) {
                if (!isValid || !scope.bid.orderCurve.length) {
                    return false;
                } else if (
                    !conversionService.validateConversionAndUpgradeAreNotBothActiveForTheSameNetworkPoint(
                        scope.bid.conversions && scope.bid.conversions[0]
                            ? scope.bid.conversions[0].conversion0
                            : undefined,
                        scope.bid.conversions && scope.bid.conversions[0]
                            ? scope.bid.conversions[0].conversion1
                            : undefined,
                        scope.bid.upgrade0,
                        scope.bid.upgrade1
                    )
                ) {
                    growl.show(
                        'It is not possible to upgrade and convert capacity at the same network point.',
                        'danger',
                        { timestamp: false }
                    );
                    return false;
                }

                scope.bid.orderCurve =
                    auctionsOverviewService.filterInvalidCurvePoint(
                        scope.bid.orderCurve,
                        'originalValue'
                    );

                if (!scope.uiCheckPage) {
                    scope.uiCheckPage = true;
                    return false;
                }

                scope.bid.orderCurve.forEach((orderCurvePoint) => {
                    if (orderCurvePoint.active) {
                        orderCurvePoint.auctionWindow =
                            scope.auction.auctionWindow;
                    }
                });

                const send = () => {
                    scope.uiSubmitting = true;

                    auctionResource
                        .bidLongTerm(
                            { auctionId: scope.auction.id },
                            scope.bid,
                            function () {
                                scope.uiSubmitAuctionSuccess = true;
                            },
                            errorHandler
                        )
                        .$promise.finally(function () {
                            scope.uiSubmitting = false;
                        });
                };

                scope.auction.incrementalAuction
                    ? informUserAboutTheIncrementalNatureOfThisAuction().result.finally(
                          send
                      )
                    : send();

                return true;
            }

            function informUserAboutTheIncrementalNatureOfThisAuction(): angular.ui.bootstrap.IModalInstanceService {
                return $uibModal.open({
                    template: `
<div class="modal-content">
    <div class="modal-body">
        <p>
            This auction is run as part of an incremental capacity process, as defined according to Articles 22 to 31 of Commission Regulation (EU) 2017/459.
            This means that alternative auctions are simultaneously available for different offer levels for the same runtime:
        </p>        
        <p class="text-left">
        <ul>
            <li ng-repeat="auctionInfo in auction.auctionsInIncrementalAuctionClosure">
                
                    <a ng-href="#/auctions/{{auctionInfo.auctionId}}/bid" target="_blank">
                     <strong><span>{{auctionInfo.auctionId}}</span></strong>
                     <span ng-if="auctionInfo.capacityCategory0 && auctionInfo.capacityCategory1"> ({{'CapacityCategory.' + auctionInfo.capacityCategory0 | translate}}/ {{'CapacityCategory.' +auctionInfo.capacityCategory1 | translate}})</span>
                     <span ng-if="auctionInfo.capacityCategory0 && !auctionInfo.capacityCategory1"> ({{'CapacityCategory.' +auctionInfo.capacityCategory0 | translate}})</span>
                     <span ng-if="!auctionInfo.capacityCategory0 && auctionInfo.capacityCategory1"> ({{'CapacityCategory.' +auctionInfo.capacityCategory1 | translate}})</span>
                    </a>
                
            </li>
        </ul>  
        </p>
        <p>
            After all the auctions for the different offer levels at this network point are closed, the involved TSO(s)
            and/ or NRA(s) will conduct an economic test. Depending on the economic test outcome, only one offer level
            will be successful. In the case where a certain shipper did not place a successful bid for the successful
            offer level auction, no capacity is allocated to the shipper.
        </p>
        <p>
            For details concerning the incremental capacity scenario and the economic test, please contact the involved
            TSO(s).
        </p>
    </div>
    <div class="modal-footer">
        <button type="button" class="btn btn-primary" ng-click="$close(true)">
            OK
        </button>
    </div>
</div>
`,
                    scope: scope,
                    controller: ($scope, $uibModalInstance) => {
                        integrateUibModelInReact($scope, $uibModalInstance);
                    }
                });
            }

            /**
             * Add another curve point to your bidding curve.
             *
             * @param {OrderCurvePointDto|{active: boolean, price: *}|{}} [curvePoint={}] If not set, an empty curve
             *  point without any preset attributes will be added.
             */
            function addAnotherSurcharge(curvePoint) {
                scope.bid.orderCurve.push(curvePoint || {});
            }

            function removeSurchargeStep(index) {
                scope.bid.orderCurve.splice(index, 1);
            }

            /**
             * Prepopulate the model with the unified bids that have already been submitted in the past.
             *
             * @param {Object} unifiedBids The future bids including the order curve.
             * @param {Object[]} unifiedBids.orderCurve The future bids including the order curve.
             */
            function prePopulateForm(unifiedBids) {
                var unifiedBidsCopied = angular.copy(unifiedBids);

                if (unifiedBidsCopied.id) {
                    unifiedBidsCopied.originOrderId = unifiedBidsCopied.id;
                    delete unifiedBidsCopied.id;
                }

                scope.bid = unifiedBidsCopied;
                scope.showUpgradeSettings = !!(
                    scope.bid.upgrade0 || scope.bid.upgrade1
                );
                scope.showConversionSettings = !!(
                    (scope.bid.conversions &&
                        scope.bid.conversions[0] &&
                        scope.bid.conversions[0].conversion0) ||
                    (scope.bid.conversions &&
                        scope.bid.conversions[0] &&
                        scope.bid.conversions[0].conversion1)
                );
            }

            /**
             * Determine the maximal capacity of the order curve with the given <tt>surcharge</tt> according to how
             * the order curve is actually ordered. The order curve is ordered by surcharge ascendingly. Based on that
             * ordering the capacity of the preceding curve point is returned. If there's no preceding order curve,
             * the general maximum permitted bid will be returned.
             *
             * @memberof pkpYourBiddingCurve
             * @param {string} surcharge
             * @returns {number}
             */
            function getQuantityOfPreviousCurvePoint(surcharge): number {
                if (!surcharge) {
                    return scope.maxBid.quantity;
                }

                var curveOrderedBySurcharge =
                    auctionsOverviewService.orderCurveBySurcharge(
                        scope.bid.orderCurve
                    );

                var i;
                for (i = 0; i < curveOrderedBySurcharge.length; i++) {
                    var curvePointSurchargeAsDecimal;
                    var matchingCurvePoint;
                    try {
                        curvePointSurchargeAsDecimal = new Decimal(
                            curveOrderedBySurcharge[i].price.originalValue.value
                        );
                        matchingCurvePoint =
                            curvePointSurchargeAsDecimal.equals(
                                new Decimal(surcharge)
                            );
                    } catch (e) {
                        continue;
                    }

                    if (matchingCurvePoint) {
                        if (i === 0) {
                            return scope.maxBid.quantity;
                        }

                        try {
                            return new Decimal(
                                curveOrderedBySurcharge[
                                    i - 1
                                ].quantity.originalValue.value
                            ).toNumber();
                        } catch (e) {
                            return scope.maxBid.quantity;
                        }
                    }
                }
            }

            function findMaxAllowedQuantity(surcharge: Decimal): number {
                console.log('findMaxAllowedQuantity');
                const quantityOfPreviousCurvePoint = new Decimal(
                    getQuantityOfPreviousCurvePoint(surcharge)
                ).toNumber();
                console.log(
                    'quantityOfPreviousCurvePoint',
                    quantityOfPreviousCurvePoint,
                    scope.bid.conversions &&
                        scope.bid.conversions[0] &&
                        scope.bid.conversions[0].conversion0 != null &&
                        scope.config.limitBidMandatoryForConversion0
                        ? scope.bid.conversions[0].conversion0.intervals
                        : null,
                    scope.bid.conversions &&
                        scope.bid.conversions[0] &&
                        scope.bid.conversions[0].conversion1 != null &&
                        scope.config.limitBidMandatoryForConversion1
                        ? scope.bid.conversions[0].conversion1.intervals
                        : null
                );
                const greatestConversion =
                    conversionService.findMaxQuantityOfConversions(
                        scope.bid.conversions &&
                            scope.bid.conversions[0] &&
                            scope.bid.conversions[0].conversion0 != null &&
                            scope.config.limitBidMandatoryForConversion0
                            ? scope.bid.conversions[0].conversion0.intervals
                            : null,
                        scope.bid.conversions &&
                            scope.bid.conversions[0] &&
                            scope.bid.conversions[0].conversion1 != null &&
                            scope.config.limitBidMandatoryForConversion1
                            ? scope.bid.conversions[0].conversion1.intervals
                            : null
                    );
                console.log('greatestConversion', greatestConversion);
                if (
                    greatestConversion != null &&
                    greatestConversion.toNumber() < quantityOfPreviousCurvePoint
                ) {
                    scope.maxBid = {
                        message:
                            'Please enter any bid amount equal to the amount requested for conversion. If you want to acquire more capacity,' +
                            ' please refrain from requesting a conversion or add another bid without placing a conversion request.',
                        quantity: quantityOfPreviousCurvePoint,
                        unit: 'kWh/h'
                    };
                    return greatestConversion.toNumber();
                } else {
                    scope.maxBid = auctionsOverviewService.calcMaxLongTermBid(
                        scope.auction.marketable,
                        scope.config.maximumOrderQuantity,
                        scope.auction.auctionWindow,
                        scope.auction.windows,
                        true
                    );
                    return quantityOfPreviousCurvePoint;
                }
            }

            function onConversionAmountChange() {
                validateFirstOrderQuantityDebounced().then(angular.noop);
            }

            function disableUpgrades() {
                delete scope.bid.upgrade0;
                delete scope.bid.upgrade1;
            }

            function disableConversions() {
                delete scope.bid.conversion0;
                delete scope.bid.conversion1;
            }

            function goBack() {
                $window.history.back();
            }
        }
    }
})();
